import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { Label, TextInput } from "flowbite-react";
import { gamesApi } from "../../api/games";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

export default function DefaultGamePackage({ isOpen, onClose, refreshGames }) {
  const { tokens, user } = useAuth();
  const [gameSettingsDropdownOpen, setGameSettingsDropdownOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState("");
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  const [name, setName] = useState("");
  const [commissionRate, setCommissionRate] = useState(0);
  const [winRate, setWinRate] = useState(0);
  const [doubleWinRate, setDoubleWinRate] = useState(0);
  const [tripleWinRate, setTripleWinRate] = useState(0);
  const [gameSettingsId, setGameSettingsId] = useState("");
  const [gameSettingsOptions, setGameSettingsOptions] = useState([]);
  const [isDefaultPackage, setIsDefaultPackage] = useState(true);
  const [thumbnail, setThumbnail] = useState({});

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setMounted(true), 10);
      fetchGameSettings();
    } else {
      setMounted(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  const fetchGameSettings = async () => {
    try {
      const response = await gamesApi.getAllGames(tokens.access.token);
      if (response) {
        setGameSettingsOptions(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching game settings:", error);
      toast.error("Failed to load game settings");
      setGameSettingsOptions([]);
    }
  };

  // Helper function to format currency
  const toCurrency = (value) => {
    return parseFloat(value).toFixed(2);
  };

  if (!visible) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Make sure the user is an admin
      if (user.roleId !== 1) {
        toast.error("Only admins can create default game packages");
        return;
      }

      const response = await gamesApi.createGame(tokens.access.token, {
        name,
        commissionRate: parseInt(commissionRate) || 0,
        winRate: parseInt(winRate) || 0,
        doubleWin: parseInt(doubleWinRate) || 0,
        tripleWin: parseInt(tripleWinRate) || 0,
        gameSettingsId,
        description: `${name} (${commissionRate}%)`,
        userId: user.id,
        gameId: null,
        isDefaultPackage: true, // Mark as a default package
        targetRoleId: 8, // Target for players (role 8)
        thumbnail: thumbnail
      });

      if (response.status === 201) {
        Swal.fire({
          title: "Success",
          text: "Default game package created successfully",
          icon: "success",
        });
        onClose();
        // Refresh games list
        if (refreshGames) {
          refreshGames();
        }
        // Reset form
        setName("");
        setCommissionRate("");
        setWinRate("");
        setDoubleWinRate("");
        setTripleWinRate("");
        setSelectedGame("");
        setGameSettingsId("");
      } else {
        Swal.fire({
          title: "Error",
          text: "Failed to create default game package",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.response.data.message,
        icon: "error",
      });
      console.error("Error creating default game package:", error);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-[60] font-poppins"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`w-full max-w-sm bg-white rounded-3xl transform transition-transform duration-300 ${mounted ? "translate-y-0" : "translate-y-full"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="py-5 bg-[#0E64D1] rounded-t-3xl text-white relative">
          <h2 className="font-bold text-center">Add Default Game Package for Players</h2>
          <button
            className="absolute right-4 top-5 text-white"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-4">
          <div className="my-3">
            <Label htmlFor="name">Package Name</Label>
            <TextInput
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Create package name"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3 relative">
            <Label>Game Type</Label>
            <button
              id="gameSettings"
              onClick={(e) => {
                e.preventDefault();
                setGameSettingsDropdownOpen(!gameSettingsDropdownOpen);
              }}
              className="w-full text-sm px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none flex justify-between"
            >
              <span className="font-light text-sm text-gray-500">
                {selectedGame || "Game Type"}
              </span>
              {gameSettingsDropdownOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            {gameSettingsDropdownOpen && (
              <ul className="absolute w-full mt-1 bg-white border rounded-md shadow-md z-10 max-h-60 overflow-y-auto">
                {Array.isArray(gameSettingsOptions) && gameSettingsOptions.map((gameSetting, index) => (
                  <li
                    key={gameSetting.id}
                    onClick={() => {
                      setSelectedGame(gameSetting.name);
                      setGameSettingsId(gameSetting.id);
                      setGameSettingsDropdownOpen(false);
                      if (typeof gameSetting.otherSettings.image === 'string') {
                        setThumbnail({ url: gameSetting.otherSettings.image });
                      } else {
                        setThumbnail(gameSetting.otherSettings.image || {});
                      }
                    }}
                    className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${
                      index !== 0 ? "border-t border-[#E1E7EF]" : ""
                    }`}
                  >
                    {gameSetting.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="my-3">
            <Label>Commission Rate (%)</Label>
            <TextInput
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3">
            <Label>Win Rate</Label>
            <TextInput
              value={winRate}
              onChange={(e) => setWinRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3">
            <Label>Double Win Rate</Label>
            <TextInput
              value={doubleWinRate}
              onChange={(e) => setDoubleWinRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3">
            <Label>Triple Win Rate</Label>
            <TextInput
              value={tripleWinRate}
              onChange={(e) => setTripleWinRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="flex gap-3 mb-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 p-2 border border-blue-600 text-blue-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 p-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Package
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 